import React from "react";
import { Tooltip, Typography } from "@mui/material";
import styled from "styled-components";
import { FormControl } from "../FormControl";
import { ValidationIssue } from "../../../rule-engine-core/Common/IDesignValidationProvider";
import { IDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/IDataDefinition";
import { PropertyEditorBoolean } from "../../../CommonUI/PropertyEditing/PropertyEditorBoolean";
import { PropertyEditorInteger } from "../../../CommonUI/PropertyEditing/PropertyEditorInteger";
import { PropertyEditorSelect } from "../../../CommonUI/PropertyEditing/PropertyEditorSelect";
import { PropertyEditorText } from "../../../CommonUI/PropertyEditing/PropertyEditorText";
import { PropertyEditorColor } from "../../../CommonUI/PropertyEditing/PropertyEditorColor";
import { DataUtilities } from "../../../rule-engine-core/Common/DataUtilities";
import { PropertyEditorCollapse } from "../../../CommonUI/PropertyEditing/PropertyEditorCollapse";
import { UISpacedBoxContent } from "../../../CommonUI/BuildlingBlocks/UISpacedBoxContent";

class XHeadingEditUI extends React.Component<{ editMe: HeadingFormControl }> {
    options: string[] = [
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
        "subtitle1",
        "subtitle2",
        "body1",
        "caption",
        "button",
        "overline",
        "srOnly",
        "inherit",
    ];

    render() {
        return (
            <UISpacedBoxContent>
                <PropertyEditorSelect
                    editObject={this.props.editMe}
                    propertyName="type"
                    options={this.options}
                />
                <PropertyEditorInteger
                    editObject={this.props.editMe}
                    propertyName="fontSize"
                    label="Font Size"
                />
                <PropertyEditorSelect
                    editObject={this.props.editMe}
                    propertyName="textAlign"
                    label="Text Align"
                    options={["left", "center", "right"]}
                />
                <PropertyEditorColor
                    editObject={this.props.editMe}
                    propertyName="color"
                    label="Color"
                />
                <PropertyEditorColor
                    editObject={this.props.editMe}
                    propertyName="background"
                    label="Background"
                />
                <PropertyEditorInteger
                    editObject={this.props.editMe}
                    propertyName="maxLength"
                    label="Max Length"
                />
                <PropertyEditorBoolean
                    editObject={this.props.editMe}
                    propertyName="fromData"
                    label="From Data?"
                />
                <PropertyEditorCollapse
                    editObject={this.props.editMe}
                    collapseLogicOrField={"fromData"}
                    showIfFalse={true}
                >
                    <PropertyEditorText
                        editObject={this.props.editMe}
                        propertyName="valueName"
                        label="Value Name For Data"
                    />
                </PropertyEditorCollapse>
                <PropertyEditorCollapse
                    editObject={this.props.editMe}
                    collapseLogicOrField={"fromData"}
                >
                    <PropertyEditorText
                        editObject={this.props.editMe}
                        propertyName="text"
                        label="Static Text"
                    />
                </PropertyEditorCollapse>{" "}
            </UISpacedBoxContent>
        );
    }
}

export class HeadingFormControl extends FormControl {
    readonly __type: string = "Heading";

    type = "h1";
    text: string = "Heading";

    valueName?: string;
    fromData: boolean = false;
    maxLength?: number = 20;

    fontSize?: number;
    color?: string;
    background?: string;

    textAlign: "left" | "center" | "right" = "left";

    constructor() {
        super();
        this.canFocus = false;
    }

    render() {
        const headText = this.fromData ? (this.valueName ? this.getFormDataValue(this.valueName) : "") : this.text;
        if (this.maxLength && (headText || "").length > this.maxLength) {
            return (
                <Tooltip title={headText}>
                    <StyledHeading
                        style={{ width: "100%" }}
                        data-testid="xheading"
                        fontSize={this.fontSize}
                        background={this.background}
                        customColor={this.color}
                        align={this.textAlign}
                    >
                        {headText.slice(0, this.maxLength)}...
                    </StyledHeading>
                </Tooltip>
            );
        }

        return (
            <StyledHeading
                style={{ width: "100%" }}
                data-testid="xheading"
                fontSize={this.fontSize}
                background={this.background}
                customColor={this.color}
                align={this.textAlign}
            >
                {headText}
            </StyledHeading>
        );
    }

    renderEditUI(): JSX.Element | null {
        return <XHeadingEditUI editMe={this} />;
    }

    getDesignValidationIssues(): ValidationIssue[] {
        return [];
    }

    getRuntimeValidationIssues(): ValidationIssue[] {
        return [];
    }

    getAddedData(): IDataDefinition | IDataDefinition[] | undefined {
        return;
    }

    getStorageData(): object {
        let retData = super.getStorageData();

        retData = DataUtilities.addStorageData(
            this,
            retData,
            "__type",
            "type",
            "text",
            "valueName",
            "fromData",
            "maxLength",
            "fontSize",
            "color",
            "background",
            "textAlign",
        );

        return retData;
    }

    setStorageData(data: object): void {
        super.setStorageData(data);

        DataUtilities.setStorageData(
            this,
            data,
            "__type",
            "type",
            "text",
            "valueName",
            "fromData",
            "maxLength",
            "fontSize",
            "color",
            "background",
            "textAlign",
        );
    }
}

const StyledHeading = styled(Typography)<{ background?: string; customColor?: string; fontSize?: number }>`
    && {
        font-style: normal;
        font-weight: 600;
        letter-spacing: 0px;

        ${(props) => (props.fontSize ? `font-size: ${props.fontSize}px;` : ``)}
        ${(props) => (props.background ? `background: ${props.background};` : ``)}
    ${(props) => (props.customColor ? `color: ${props.customColor};` : ``)}
    }
`;
